import React, { useEffect, useState } from 'react';
import {
  Grid,
  Button,
  InputLabel,
  FormControl,
  MenuItem,
  OutlinedInput,
  CircularProgress,
  TextareaAutosize,
  Box,
  FormHelperText,
  Checkbox,
  Select,
  Typography,
  TextFieldProps,
  TextField,
} from '@mui/material';
import { DateRange } from '@mui/icons-material';

import momentTZ from 'moment-timezone';

import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useMutation } from '@tanstack/react-query';
import { useGetLists } from 'services/lists';
import { createJob, Job } from 'services/jobs';
import { daysOfWeek, ispList } from '../../constants';
import { useGetMailwizzLists } from 'services/lists';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

interface Props {
  onSuccess: () => void;
}

const getMinLaunchDateEST = () => momentTZ.tz('America/New_York').subtract(1, 'day').startOf('day').toDate();

export const statuses = ['Off', 'Finished', 'Pending', 'Failed', 'Running'];

const CreateNewJob: React.FC<Props> = ({ onSuccess }) => {
  const { data: lists } = useGetLists();
  const { data: mailwizzLists, error: errorListMailwizz, isLoading: isLoadingListMailwizz } = useGetMailwizzLists();

  const { isError, isLoading, mutate } = useMutation((job: Partial<Job>) => createJob(job), {
    onError: (error) => {
      console.log(error);
    },
    onSuccess: onSuccess,
  });

  const initialValues = {
    list_ids: [] as any[],
    name: '',
    scrubbedProfileForEmail: 100000,
    notes: '',
    recurring: true,
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false,
    status: 'PENDING',
    includeISP: [] as string[],
    ispDetails: {} as Record<string, { value?: number }>,
    // MailWizz only
    mailwizz_list: '',
    selected_list: 'mailwizz',
    launchDate: '',
    submit: '',
  };

  const validationSchema = Yup.object().shape({
    list_ids: Yup.array().required('List is required').min(1, 'At least one list is required'),
    scrubbedProfileForEmail: Yup.number().required('Scrubbed Profiles to be Emailed Required').lessThan(10000000),
    notes: Yup.string(),
    name: Yup.string().required('Name is Required'),
    recurring: Yup.boolean(),
    monday: Yup.boolean(),
    tuesday: Yup.boolean(),
    wednesday: Yup.boolean(),
    thursday: Yup.boolean(),
    friday: Yup.boolean(),
    saturday: Yup.boolean(),
    sunday: Yup.boolean(),
    status: Yup.string(),
    includeISP: Yup.array().required('Include ISP required').min(1, 'At least one ISP required'),
    ispDetails: Yup.object().shape({}),
    launchDate: Yup.string().required('Launch Date required'),
    // locked to mailwizz, but keep schema to match original shape
    selected_list: Yup.string().required('Selected List required'),
    mailwizz_list: Yup.string().required('MailWizz List is Required'),
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRemember, setIsRemember] = useState(false);

  const { errors, values, touched, handleBlur, handleChange, handleSubmit, validateForm, setFieldValue, setStatus, setErrors } =
    useFormik({
      initialValues,
      validationSchema,
      onSubmit: async (values: any) => {
        console.log('values: ', values);
        try {
          setIsSubmitting(true);
          if (values.list_ids.length > 0 && values.includeISP.length > 0) {
            if (
              values.sunday ||
              values.monday ||
              values.tuesday ||
              values.wednesday ||
              values.thursday ||
              values.friday ||
              values.saturday
            ) {
              // Keep the original payload shape/keys exactly as before
              mutate({
                ...values,
                scrubbedProfileForEmail: parseInt('' + values.scrubbedProfileForEmail),
              });
            }
          }
        } catch (e) {
          console.log(e);
          if (e instanceof Error) {
            setStatus({ success: false });
            setErrors({ submit: e.message });
          }
        } finally {
          setIsSubmitting(false);
        }
      },
    });

  const getSum = () => {
    let sum = 0;
    values.includeISP.map((item: any) => {
      sum += values.ispDetails[item]?.value ? (values.ispDetails[item]?.value * values.scrubbedProfileForEmail) / 100 : 0;
    });
    return sum;
  };

  useEffect(() => {
    validateForm();
  }, [values, validateForm]);

  return (
    <Box>
      <form onSubmit={handleSubmit}>
        {isLoadingListMailwizz ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Typography variant="h3">Loading...</Typography>
          </Box>
        ) : lists ? (
          <Grid container direction="column" spacing={2} p={2} pb={0} pt={1}>
            <Grid item>
              <InputLabel htmlFor="outlined-adornment-email-login">Name</InputLabel>
              <FormControl fullWidth error={Boolean(touched.name && errors.name)}>
                <OutlinedInput
                  id="outlined-adornment-email-login"
                  type="text"
                  name="name"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.name}
                />
              </FormControl>
              {touched.name && errors.name && (
                <FormHelperText error>{typeof errors.name === 'string' ? errors.name : 'An error occurred'}</FormHelperText>
              )}
            </Grid>

            <Grid item container spacing={2}>
              <Grid item xs={12} md={6}>
                <InputLabel htmlFor="outlined-adornment-email-login">Select List</InputLabel>
                <FormControl fullWidth error={Boolean(touched.list_ids && errors.list_ids)}>
                  <Select
                    multiple
                    id="list_ids"
                    name="list_ids"
                    value={values.list_ids}
                    onBlur={handleBlur}
                    onChange={(e) => {
                      const { value } = e.target as any;
                      setFieldValue('list_ids', typeof value === 'string' ? value.split(',') : value);
                    }}
                  >
                    {lists.length > 0
                      ? lists.map((list: any) => (
                          <MenuItem key={list.id} value={list.id}>
                            {list.list_name}
                          </MenuItem>
                        ))
                      : 'No Lists'}
                  </Select>
                </FormControl>
                {touched.list_ids && errors.list_ids && (
                  <FormHelperText error>
                    {typeof errors.list_ids === 'string' ? errors.list_ids : 'An error occurred'}
                  </FormHelperText>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <InputLabel htmlFor="outlined-adornment-email-login">Scrubbed Profiles to be Emailed</InputLabel>
                <FormControl fullWidth error={Boolean(touched.scrubbedProfileForEmail && errors.scrubbedProfileForEmail)}>
                  <OutlinedInput
                    id="scrubbedProfileForEmail"
                    type="number"
                    name="scrubbedProfileForEmail"
                    value={values.scrubbedProfileForEmail}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    inputProps={{}}
                  />
                </FormControl>
              </Grid>
            </Grid>

            <Grid item container spacing={2}>
              <Grid item xs={12} md={6}>
                <InputLabel htmlFor="outlined-adornment-email-login">Recurring?</InputLabel>
                <FormControl error={Boolean(touched.recurring && errors.recurring)}>
                  <Checkbox
                    checked={values.recurring}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    name="recurring"
                    inputProps={{ 'aria-label': 'controlled' }}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6} container spacing={2}>
                {daysOfWeek.map((day) => (
                  <Grid item key={day}>
                    <InputLabel htmlFor="outlined-adornment-email-login">{capitalizeFirstLetter(day)}</InputLabel>
                    <FormControl fullWidth error={Boolean((touched as any)[day] && (errors as any)[day])}>
                      <Checkbox
                        checked={(values as any)[day]}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        name={day}
                        inputProps={{ 'aria-label': 'controlled' }}
                      />
                    </FormControl>
                  </Grid>
                ))}
                {touched.sunday &&
                values.sunday !== true &&
                touched.monday &&
                values.monday !== true &&
                touched.tuesday &&
                values.tuesday !== true &&
                touched.wednesday &&
                values.wednesday !== true &&
                touched.thursday &&
                values.thursday !== true &&
                touched.friday &&
                values.friday !== true &&
                touched.saturday &&
                values.saturday !== true ? (
                  <Typography className="error-message">Days are Required</Typography>
                ) : (
                  ''
                )}
              </Grid>
            </Grid>

            {/* Status */}
            <Grid item container spacing={2}>
              <Grid item xs={12}>
                <InputLabel htmlFor="outlined-adornment-email-login">Status</InputLabel>
                <FormControl fullWidth>
                  <Select id="status" name="status" value={values.status} onBlur={handleBlur} onChange={handleChange}>
                    {statuses.map((status) => (
                      <MenuItem key={status} value={status.toUpperCase()}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Select Mailwizz only */}
            <Grid item container spacing={2}>
              <Grid item xs={12} md={6}>
                <InputLabel htmlFor="outlined-adornment-email-login">Select List</InputLabel>
                <FormControl fullWidth error={Boolean(touched.selected_list && errors.selected_list)}>
                  <Select
                    id="selected_list"
                    name="selected_list"
                    value={values.selected_list}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  >
                    <MenuItem key={0} value={'mailwizz'}>
                      Mailwizz
                    </MenuItem>
                  </Select>
                </FormControl>
                {touched.selected_list && errors.selected_list && (
                  <FormHelperText error>
                    {typeof errors.selected_list === 'string' ? errors.selected_list : 'An error occurred'}
                  </FormHelperText>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <>
                  <InputLabel htmlFor="outlined-adornment-email-login">Mailwizz List</InputLabel>
                  <FormControl fullWidth error={Boolean(touched.mailwizz_list && errors.mailwizz_list)}>
                    <Select
                      id="mailwizz_list"
                      name="mailwizz_list"
                      value={values.mailwizz_list}
                      onBlur={handleBlur}
                      onChange={handleChange}
                    >
                      {mailwizzLists && mailwizzLists?.count && parseInt(mailwizzLists.count.toString()) > 0 ? (
                        mailwizzLists.records.map((list: any) => (
                          <MenuItem key={list.general.list_uid} value={list.general.list_uid}>
                            {list.general.name}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem disabled={true} key={'nodata'} value={''}>
                          {'No Mailwizz List Found'}
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                  {touched.mailwizz_list && errors.mailwizz_list && values.selected_list === 'mailwizz' && (
                    <FormHelperText error>
                      {typeof errors.mailwizz_list === 'string' ? errors.mailwizz_list : 'An error occurred'}
                    </FormHelperText>
                  )}
                </>
              </Grid>
            </Grid>

            <Grid item container spacing={2}>
              <Grid item xs={12} md={6}>
                <InputLabel htmlFor="outlined-adornment-include-isp">Include ISP</InputLabel>
                <FormControl fullWidth error={Boolean(touched.includeISP && errors.includeISP)}>
                  <Select
                    multiple
                    id="includeISP"
                    name="includeISP"
                    value={values.includeISP}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  >
                    {ispList.map((list) => (
                      <MenuItem key={list} value={list}>
                        {list}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {touched.includeISP && errors.includeISP && (
                  <FormHelperText error>
                    {typeof errors.includeISP === 'string' ? errors.includeISP : 'An error occurred'}
                  </FormHelperText>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <InputLabel htmlFor="outlined-adornment-email-login">Launch Date</InputLabel>
                <FormControl fullWidth error={Boolean(touched.launchDate && errors.launchDate)}>
                  <DatePicker
                    className={`date-picker ${touched.launchDate && errors.launchDate ? 'date-border' : ''} date-outline`}
                    minDate={getMinLaunchDateEST()}
                    onChange={(e: any) => {
                      const value = moment(e).format('YYYY-MM-DD');
                      setFieldValue('launchDate', value);
                    }}
                    value={values.launchDate}
                    selected={values?.launchDate ? new Date(moment(values.launchDate).format('YYYY-MM-DD')) : new Date()}
                    showIcon
                    icon={<DateRange />}
                  />
                  {touched.launchDate && errors.launchDate && (
                    <FormHelperText error>
                      {typeof errors.launchDate === 'string' ? errors.launchDate : 'An error occurred'}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
            </Grid>

            {/* ip number add fields */}
            <Grid item container spacing={2}>
              <Grid item xs={12} md={6}>
                <InputLabel htmlFor="outlined-adornment-include-isp">Number Of ISP</InputLabel>

                {values.includeISP && values.includeISP.length > 0 ? (
                  <Box>
                    {values.includeISP.map((name: any, index: number) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          gap: 2,
                          marginBottom: '10px',
                          justifyContent: 'left',
                          alignItems: 'center',
                        }}
                      >
                        <FormControl fullWidth error={Boolean(touched.ispDetails && errors.ispDetails)}>
                          <OutlinedInput
                            id="outlined-adornment-email-login"
                            type="text"
                            name={`ispDetails.${name}.value`}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.ispDetails[name]?.value}
                          />
                        </FormControl>
                        <Typography>%</Typography>
                        <Typography>{name}</Typography>
                        <Box>
                          {values.ispDetails[name]?.value
                            ? (values.ispDetails[name]?.value * values.scrubbedProfileForEmail) / 100
                            : 0}
                        </Box>
                      </Box>
                    ))}
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 2,
                        marginBottom: '10px',
                        justifyContent: 'right',
                        alignItems: 'center',
                      }}
                    >
                      <Box>{getSum()}</Box>
                    </Box>
                  </Box>
                ) : (
                  ''
                )}
              </Grid>
            </Grid>

            <Grid item>
              <InputLabel htmlFor="outlined-adornment-email-login">Notes</InputLabel>
              <FormControl fullWidth error={Boolean(touched.notes && errors.notes)}>
                <TextareaAutosize name="notes" minRows={3} onChange={handleChange} onBlur={handleBlur} />
              </FormControl>
            </Grid>
          </Grid>
        ) : (
          'No Data Found'
        )}

        {isError && (
          <Box sx={{ mt: 3 }}>
            <FormHelperText error>{'Failed to Create New Job'}</FormHelperText>
          </Box>
        )}
        <Grid item container>
          <Button
            sx={{ mt: 3 }}
            disableElevation
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            color="primary"
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress /> : 'Create New Job'}
          </Button>
        </Grid>
      </form>
    </Box>
  );
};

function capitalizeFirstLetter(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export default CreateNewJob;
